import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import {
  Bot,
  BrainCircuit,
  CheckCircle2,
  ChevronRight,
  Command,
  Menu,
  MessagesSquare,
  Radar,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { HeroAiInfrastructure } from '../../components/hero-ai-infrastructure'
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet'

const navLinks = [
  { label: 'Platform', href: '#platform' },
  { label: 'Compare', href: '#compare' },
  { label: 'Use Cases', href: '#use-cases' },
  { label: 'Docs', href: '/developers' },
]

const proofStats = [
  { label: 'Train on any data', value: 'Text, URLs, Files', note: 'We handle chunking, embeddings, and vector search.' },
  { label: 'Per-user isolation', value: 'One bot, many users', note: 'Variables like user_id keep each user scoped to their own data.' },
  { label: 'Tools + skills', value: 'HTTP, MCP, Sandbox', note: 'Connect APIs, actions, and reusable capabilities.' },
  { label: 'Embed anywhere', value: 'Widget + API', note: 'Drop agents into your website, product, or customer portal.' },
]

const capabilities = [
  {
    icon: BrainCircuit,
    title: 'Train on your data',
    description: 'Upload text, URLs, and files. Your agent answers from your knowledge, not generic model memory.',
  },
  {
    icon: Command,
    title: 'Connect any tool',
    description: 'Add HTTP tools, MCP servers, and actions so the agent can do work, not just reply.',
  },
  {
    icon: Radar,
    title: 'Add skills',
    description: 'Package reusable capabilities like search, lead capture, sandbox scripts, or custom integrations.',
  },
  {
    icon: ShieldCheck,
    title: 'Isolate every user',
    description: 'Pass variables like user_id or tenant_id so each customer chats with their own data safely.',
  },
]

const workflowSteps = [
  {
    title: 'Create',
    description: 'Choose model, prompt, and variables.',
  },
  {
    title: 'Train',
    description: 'Upload content. We handle chunking and vectors.',
  },
  {
    title: 'Connect',
    description: 'Add tools, MCP, and skills.',
  },
  {
    title: 'Embed',
    description: 'Use the widget or API inside your app.',
  },
]

const oldWay = [
  'Wire models, prompts, and vector search yourself',
  'Build tool calling, sessions, and identity from scratch',
  'Solve multi-tenant user data isolation on your own',
  'Keep maintaining the whole agent stack',
]

const newWay = [
  'Create the agent and train it on real customer data',
  'Attach tools, MCP servers, and reusable skills',
  'Pass user_id or tenant_id and isolate each user automatically',
  'Ship in your website or SaaS with widget, API, and handoff built in',
]

const useCases = [
  {
    title: 'Support on your website',
    description: 'Train an agent on docs, FAQs, and product knowledge for instant support.',
    outcome: 'Answer faster and hand off only when needed.',
  },
  {
    title: 'Agents inside your product',
    description: 'Let each customer chat with their own data inside your app or portal.',
    outcome: 'One platform, many end users, isolated data.',
  },
  {
    title: 'Action-ready copilots',
    description: 'Connect tools and skills so the agent can search, update systems, and trigger workflows.',
    outcome: 'Less manual ops, more useful automation.',
  },
]

const motionIn = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
}

const landingTheme = {
  colorScheme: 'light',
  '--background': '154 24% 98%',
  '--foreground': '174 34% 12%',
  '--card': '0 0% 100%',
  '--card-foreground': '174 34% 12%',
  '--popover': '0 0% 100%',
  '--popover-foreground': '174 34% 12%',
  '--primary': '165 43% 30%',
  '--primary-foreground': '0 0% 100%',
  '--secondary': '156 14% 94%',
  '--secondary-foreground': '174 34% 12%',
  '--muted': '156 14% 94%',
  '--muted-foreground': '168 12% 36%',
  '--accent': '156 14% 94%',
  '--accent-foreground': '174 34% 12%',
  '--border': '160 17% 84%',
  '--input': '160 17% 84%',
  '--ring': '165 43% 30%',
} as CSSProperties

const landingSans = '"Plus Jakarta Sans", "Outfit", system-ui, sans-serif'
const landingSerif = '"Cormorant Garamond", Georgia, serif'

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-3 rounded-[1.35rem] border border-[#0d3531]/10 bg-[linear-gradient(180deg,#ffffff,rgba(247,249,246,0.98))] px-3 py-3 shadow-[0_10px_30px_rgba(13,53,49,0.05)]">
      <div className="flex h-9 w-9 items-center justify-center rounded-2xl border border-[#0d3531]/8 bg-[#eef5f1] text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
        <Sparkles className="h-4 w-4" />
      </div>
      <span className="pr-2 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-[#1f5a52]">
        {children}
      </span>
    </div>
  )
}

function SectionIntro({
  label,
  title,
  description,
}: {
  label: string
  title: string
  description: string
}) {
  return (
    <motion.div className="mx-auto max-w-3xl text-center" {...motionIn}>
      <SectionLabel>{label}</SectionLabel>
      <h2 className="mt-6 text-3xl font-semibold tracking-[-0.04em] text-foreground sm:text-4xl lg:text-5xl">
        {title}
      </h2>
      <p className="mt-5 text-base leading-8 text-muted-foreground sm:text-lg">
        {description}
      </p>
    </motion.div>
  )
}

function AgentMeshGraphic() {
  const reduceMotion = useReducedMotion()

  return (
    <div className="relative mx-auto w-full max-w-[620px]">
      <motion.div
        className="absolute -left-8 top-10 h-24 w-24 rounded-full bg-primary/12 blur-3xl"
        animate={reduceMotion ? undefined : { y: [0, -18, 0], x: [0, 12, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute -right-4 bottom-12 h-28 w-28 rounded-full bg-emerald-300/30 blur-3xl"
        animate={reduceMotion ? undefined : { y: [0, 14, 0], x: [0, -12, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(247,250,248,0.92))] p-4 shadow-[0_30px_120px_rgba(21,77,68,0.14)]">
        <div className="rounded-[1.7rem] border border-primary/10 bg-[radial-gradient(circle_at_top,rgba(82,180,160,0.16),transparent_42%),linear-gradient(180deg,#ffffff,#f5faf8)] p-4 sm:p-6">
          <svg viewBox="0 0 560 460" className="h-auto w-full">
            <defs>
              <linearGradient id="signalStroke" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(48,129,112,0.18)" />
                <stop offset="50%" stopColor="rgba(48,129,112,0.72)" />
                <stop offset="100%" stopColor="rgba(121,199,163,0.2)" />
              </linearGradient>
              <radialGradient id="coreGlow" cx="50%" cy="50%" r="65%">
                <stop offset="0%" stopColor="rgba(39,130,111,0.38)" />
                <stop offset="100%" stopColor="rgba(39,130,111,0)" />
              </radialGradient>
            </defs>

            {[
              'M112 122C168 122 190 168 238 188C294 210 327 162 392 162',
              'M128 326C192 326 206 290 250 264C312 228 340 294 430 294',
              'M118 226C176 226 214 226 280 226C346 226 382 226 440 226',
            ].map((path, index) => (
              <motion.path
                key={path}
                d={path}
                fill="none"
                stroke="url(#signalStroke)"
                strokeWidth="4"
                strokeLinecap="round"
                initial={{ pathLength: 0.2, opacity: 0.45 }}
                animate={reduceMotion ? { pathLength: 1, opacity: 0.8 } : { pathLength: [0.2, 1, 0.2], opacity: [0.45, 0.9, 0.45] }}
                transition={{
                  duration: 4.8 + index,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: index * 0.35,
                }}
              />
            ))}

            <motion.circle
              cx="280"
              cy="226"
              r="100"
              fill="url(#coreGlow)"
              animate={reduceMotion ? undefined : { scale: [1, 1.08, 1] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.circle
              cx="280"
              cy="226"
              r="52"
              fill="white"
              stroke="rgba(39,130,111,0.18)"
              strokeWidth="2"
              animate={reduceMotion ? undefined : { scale: [1, 1.04, 1] }}
              transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
            />
            <g>
              <circle cx="280" cy="226" r="34" fill="rgba(39,130,111,0.12)" />
              <path d="M262 224c0-10 8-18 18-18s18 8 18 18-8 18-18 18" fill="none" stroke="rgba(39,130,111,0.85)" strokeWidth="5" strokeLinecap="round" />
              <path d="M286 207l13 18-13 18" fill="none" stroke="rgba(39,130,111,0.85)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            </g>

            {[
              { x: 92, y: 104, title: 'Intent', subtitle: 'understood', icon: 'I' },
              { x: 394, y: 140, title: 'CRM', subtitle: 'updated', icon: 'C' },
              { x: 78, y: 302, title: 'Knowledge', subtitle: 'retrieved', icon: 'K' },
              { x: 404, y: 272, title: 'Handoff', subtitle: 'ready', icon: 'H' },
            ].map((node, index) => (
              <motion.g
                key={node.title}
                animate={reduceMotion ? undefined : { y: [0, index % 2 === 0 ? -8 : 8, 0] }}
                transition={{ duration: 4 + index, repeat: Infinity, ease: 'easeInOut' }}
              >
                <rect
                  x={node.x}
                  y={node.y}
                  rx="18"
                  width="96"
                  height="72"
                  fill="rgba(255,255,255,0.92)"
                  stroke="rgba(39,130,111,0.16)"
                />
                <circle cx={node.x + 22} cy={node.y + 24} r="12" fill="rgba(39,130,111,0.12)" />
                <text x={node.x + 22} y={node.y + 29} textAnchor="middle" fontSize="11" fill="rgba(39,130,111,0.85)" fontWeight="700">
                  {node.icon}
                </text>
                <text x={node.x + 22} y={node.y + 52} fontSize="13" fill="#111827" fontWeight="700">
                  {node.title}
                </text>
                <text x={node.x + 22} y={node.y + 66} fontSize="11" fill="#6b7280">
                  {node.subtitle}
                </text>
              </motion.g>
            ))}

            {[
              { cx: 236, cy: 188, delay: 0 },
              { cx: 324, cy: 188, delay: 0.7 },
              { cx: 234, cy: 264, delay: 1.4 },
              { cx: 326, cy: 264, delay: 2.1 },
            ].map((pulse) => (
              <motion.circle
                key={`${pulse.cx}-${pulse.cy}`}
                cx={pulse.cx}
                cy={pulse.cy}
                r="6"
                fill="rgba(39,130,111,0.75)"
                animate={reduceMotion ? undefined : { r: [6, 11, 6], opacity: [0.9, 0.35, 0.9] }}
                transition={{ duration: 2.4, repeat: Infinity, delay: pulse.delay }}
              />
            ))}
          </svg>

        </div>

      </div>
    </div>
  )
}

export const LandingPage: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div
      style={{ ...landingTheme, fontFamily: landingSans }}
      className="min-h-screen overflow-x-hidden bg-[#f6f7f3] text-foreground selection:bg-primary/20"
    >
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <motion.div
          className="absolute left-[-8%] top-24 h-[30rem] w-[30rem] rounded-full bg-primary/8 blur-3xl"
          animate={reduceMotion ? undefined : { x: [0, 50, 0], y: [0, -30, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute right-[-10%] top-[28rem] h-[34rem] w-[34rem] rounded-full bg-emerald-200/20 blur-3xl"
          animate={reduceMotion ? undefined : { x: [0, -40, 0], y: [0, 36, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(12,49,46,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(12,49,46,0.04)_1px,transparent_1px)] bg-[size:88px_88px] [mask-image:radial-gradient(circle_at_center,black,transparent_82%)]" />
      </div>

      <nav className="fixed left-0 right-0 top-4 z-50 px-4">
        <div className="mx-auto max-w-7xl">
          <div
            className={`flex items-center justify-between rounded-[1.4rem] border px-5 py-3 transition-all duration-300 sm:px-6 ${
              scrolled
                ? 'border-white/10 bg-[#0c2f2d]/88 shadow-[0_20px_60px_rgba(6,23,21,0.28)] backdrop-blur-xl'
                : 'border-white/10 bg-[#0c2f2d]/70 shadow-[0_8px_40px_rgba(6,23,21,0.18)] backdrop-blur-md'
            }`}
          >
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,hsl(var(--primary)),#5b9d8a)] text-white shadow-[0_14px_30px_rgba(18,62,57,0.35)]">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-200/90">CleverAct AI</p>
                <p className="text-sm text-white/55">Agent infrastructure platform</p>
              </div>
            </Link>

            <div className="hidden items-center gap-8 md:flex">
              {navLinks.map((link) =>
                link.href.startsWith('#') ? (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-sm font-medium text-white/70 transition-colors hover:text-white"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-sm font-medium text-white/70 transition-colors hover:text-white"
                  >
                    {link.label}
                  </Link>
                ),
              )}
            </div>

            <div className="hidden items-center gap-3 md:flex">
              <Link to="/auth/login">
                <Button variant="ghost" className="rounded-full px-5 text-sm font-semibold text-white hover:bg-white/10 hover:text-white">
                  Log in
                </Button>
              </Link>
              <Link to="/auth/register">
                <Button className="rounded-full border border-white/10 bg-[#71c4a4] px-6 py-2.5 text-sm font-semibold text-[#0b2e2b] shadow-[0_16px_32px_rgba(7,41,41,0.18)] hover:bg-[#84d1b2]">
                  Start Free
                </Button>
              </Link>
            </div>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className="rounded-xl border border-white/10 bg-white/10 p-2.5 text-white shadow-sm md:hidden"
                  aria-label="Menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 border-border bg-white/95">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <div className="mt-10 flex flex-col gap-4">
                  {navLinks.map((link) =>
                    link.href.startsWith('#') ? (
                      <a
                        key={link.label}
                        href={link.href}
                        onClick={() => setMobileOpen(false)}
                        className="rounded-2xl border border-primary/10 px-4 py-3 text-base font-medium text-foreground"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.label}
                        to={link.href}
                        onClick={() => setMobileOpen(false)}
                        className="rounded-2xl border border-primary/10 px-4 py-3 text-base font-medium text-foreground"
                      >
                        {link.label}
                      </Link>
                    ),
                  )}
                  <div className="mt-4 grid gap-3">
                    <Link to="/auth/login" onClick={() => setMobileOpen(false)}>
                      <Button variant="outline" className="w-full rounded-full">
                        Log in
                      </Button>
                    </Link>
                    <Link to="/auth/register" onClick={() => setMobileOpen(false)}>
                      <Button className="w-full rounded-full bg-[linear-gradient(135deg,hsl(var(--primary)),#63bc98)] text-white">
                        Start Free
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <main className="relative z-10">
        <HeroAiInfrastructure serifFont={landingSerif} />

        <section className="px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <motion.div
              className="-mt-20 grid gap-4 rounded-[2rem] border border-[#0d3531]/8 bg-[#f8f8f3]/94 p-5 shadow-[0_25px_80px_rgba(16,24,40,0.08)] backdrop-blur sm:grid-cols-2 lg:grid-cols-4 lg:p-7"
              {...motionIn}
            >
              {proofStats.map((stat) => (
                <div key={stat.label} className="rounded-[1.5rem] border border-[#0d3531]/8 bg-white p-5">
                  <p className="text-2xl font-semibold tracking-[-0.04em] text-[#0d3531] sm:text-3xl">{stat.value}</p>
                  <p className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-primary/85">{stat.label}</p>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{stat.note}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <section id="platform" className="px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <SectionIntro
              label="Platform"
              title="Everything needed to ship an agent, without building the stack yourself."
              description="Knowledge, tools, skills, embedding, and user isolation in one platform."
            />

            <div className="mt-16 grid gap-5 lg:grid-cols-2">
              {capabilities.map((capability, index) => {
                const Icon = capability.icon
                return (
                  <motion.div
                    key={capability.title}
                    className="group relative overflow-hidden rounded-[2rem] border border-primary/10 bg-white/82 p-8 shadow-[0_22px_70px_rgba(16,24,40,0.06)]"
                    {...motionIn}
                    transition={{ ...motionIn.transition, delay: index * 0.08 }}
                  >
                    <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,transparent,hsl(var(--primary)),transparent)] opacity-50" />
                    <div className="flex items-start gap-5">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">{capability.title}</h3>
                        <p className="mt-4 max-w-xl text-base leading-8 text-muted-foreground">
                          {capability.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        <section id="compare" className="px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <SectionIntro
              label="Compare"
              title="The old way is building agent infrastructure. The new way is shipping agents."
              description="Most teams do not want to build RAG, vector search, tools, sessions, and tenant isolation from scratch."
            />

            <div className="mt-16 grid gap-5 lg:grid-cols-2">
              <motion.div
                className="rounded-[2rem] border border-rose-200/70 bg-[linear-gradient(180deg,rgba(255,247,247,0.96),rgba(255,255,255,0.92))] p-7 shadow-[0_22px_60px_rgba(16,24,40,0.05)]"
                {...motionIn}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
                    <MessagesSquare className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-rose-600/80">Old way</p>
                    <p className="text-lg font-semibold text-foreground">Build the stack yourself</p>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  {oldWay.map((item) => (
                    <div key={item} className="rounded-2xl border border-rose-100 bg-white/85 px-4 py-3 text-sm leading-6 text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                className="rounded-[2rem] border border-emerald-200/80 bg-[linear-gradient(180deg,rgba(242,251,247,0.96),rgba(255,255,255,0.94))] p-7 shadow-[0_22px_60px_rgba(16,24,40,0.05)]"
                {...motionIn}
                transition={{ ...motionIn.transition, delay: 0.08 }}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700/80">New way</p>
                    <p className="text-lg font-semibold text-foreground">Use CleverAct</p>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  {newWay.map((item) => (
                    <div key={item} className="rounded-2xl border border-emerald-100 bg-white/88 px-4 py-3 text-sm leading-6 text-slate-800">
                      {item}
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section id="use-cases" className="px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <SectionIntro
              label="Use cases"
              title="One platform for revenue, support, and activation."
              description="Agentic AI only matters if it improves concrete business outcomes. These are the jobs the landing page should make obvious at a glance."
            />

            <div className="mt-16 grid gap-5 lg:grid-cols-3">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={useCase.title}
                  className="relative overflow-hidden rounded-[2rem] border border-primary/10 bg-white/84 p-7 shadow-[0_22px_60px_rgba(16,24,40,0.06)]"
                  {...motionIn}
                  transition={{ ...motionIn.transition, delay: index * 0.08 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="rounded-full border border-primary/10 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary/80">
                      0{index + 1}
                    </div>
                    <ChevronRight className="h-5 w-5 text-primary/60" />
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold tracking-[-0.03em] text-foreground">{useCase.title}</h3>
                  <p className="mt-4 text-base leading-8 text-muted-foreground">{useCase.description}</p>
                  <div className="mt-8 rounded-2xl bg-[linear-gradient(180deg,rgba(239,248,244,0.95),rgba(255,255,255,0.9))] px-4 py-4 text-sm font-medium leading-7 text-foreground">
                    {useCase.outcome}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl rounded-[2.5rem] border border-primary/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.96),rgba(243,249,246,0.94))] p-8 shadow-[0_28px_90px_rgba(16,24,40,0.08)] sm:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[1fr_0.95fr] lg:items-center">
              <motion.div {...motionIn}>
                <SectionLabel>How it ships</SectionLabel>
                <h2 className="mt-6 text-4xl font-semibold tracking-[-0.05em] text-foreground sm:text-5xl">
                  Four steps from blank page to embedded agent.
                </h2>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
                  Keep it simple: create the agent, train it, connect what it needs, then ship it into the product.
                </p>
              </motion.div>

              <motion.div className="grid gap-4" {...motionIn}>
                {workflowSteps.map((step, index) => (
                  <div key={step.title} className="flex items-start gap-4 rounded-[1.6rem] border border-primary/10 bg-white/82 px-5 py-5 shadow-sm">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                      0{index + 1}
                    </div>
                    <div>
                      <p className="text-base font-semibold text-foreground">{step.title}</p>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        <section className="px-4 pb-24 pt-8 sm:px-6 lg:px-8 lg:pb-32">
          <div className="mx-auto max-w-7xl">
            <motion.div
              className="relative overflow-hidden rounded-[2.6rem] border border-[#0a2b29]/10 bg-[linear-gradient(135deg,#0a2b29_0%,#13413c_100%)] px-8 py-10 text-white shadow-[0_36px_90px_rgba(7,41,41,0.24)] sm:px-10 sm:py-12 lg:px-14 lg:py-16"
              {...motionIn}
            >
              <div className="absolute right-0 top-0 h-56 w-56 translate-x-1/4 -translate-y-1/4 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute bottom-0 left-0 h-56 w-56 -translate-x-1/4 translate-y-1/4 rounded-full bg-emerald-200/14 blur-3xl" />
              <div className="relative z-10 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-white/70">Launch a better first impression</p>
                  <h2 className="mt-5 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">
                    Ship the agent layer your product should already have.
                  </h2>
                  <p className="mt-6 max-w-2xl text-lg leading-8 text-white/80">
                    Train it on data, connect the right tools, and embed it where your users already work.
                  </p>
                </div>
                <div className="flex flex-col gap-4 sm:flex-row lg:justify-end">
                  <Link to="/auth/register">
                    <Button className="h-14 rounded-full bg-[#8fd5b5] px-7 text-base font-semibold text-[#0a2b29] hover:bg-[#9cdbbe]">
                      Start Free
                    </Button>
                  </Link>
                  <Link to="/developers">
                    <Button variant="outline" className="h-14 rounded-full border-white/15 bg-white/5 px-7 text-base font-semibold text-white hover:bg-white/10 hover:text-white">
                      View Docs
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-[#0d3531]/8 bg-[#f1f2ec] px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">CleverAct AI</p>
              <p className="text-sm text-muted-foreground">Agents trained on data, tools, and skills</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <Link to="/developers" className="transition-colors hover:text-foreground">Docs</Link>
            <Link to="/auth/login" className="transition-colors hover:text-foreground">Log in</Link>
            <Link to="/auth/register" className="transition-colors hover:text-foreground">Get started</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
